import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Grid,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box,
  Radio,
  IconButton
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import {
  calculateAge,
  dropdownFetch,
  getCityByState,
  getCurrentDateTime,
  getKeyPathValue,
  getStatesByCountry,
  request,
  toast
} from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { GENDER_DROPDOWN } from '../../../../../constants/dropdown';

const initialPatientDataObj = {
  data: [],
  loading: false
};
const initialDonorDataObj = {
  data: [],
  loading: false
};

const TABLE_COLUMNS = [
  {
    header: 'Donor Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.patient_name}`}
        </span>
      );
    }
  },
  {
    header: 'Gender',
    accessor: 'gender'
  },
  {
    header: 'Age',
    accessor: (row) => calculateAge(row?.dob)
  },
  {
    header: 'Blood Group',
    accessor: (row) => row?.blood_type?.group
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

export const DonarMaster = () => {
  const doctorIDRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [donorDataObj, setDonorDataObj] = useState(initialDonorDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState('donated_yes');
  const [posData, setPosData] = useState([]);
  const [bloodGroupDropdown, setBloodGroupDropdown] = useState([]);
  const [input, setInput] = useState('');

  const { getValues } = useForm();
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        // location_to: Yup.string().required(' '),
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      country: 'IN'
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const getBloodGroupDropdown = useCallback(async () => {
    const data = await dropdownFetch('blood-bank-group');
    setBloodGroupDropdown(data);
  }, []);

  useEffect(() => {
    getBloodGroupDropdown();
  }, [getBloodGroupDropdown]);

  const handleRadioChange = (event) => {
    const { name } = event.target;
    setSelectedRadio(name);
  };

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', 'dob', 'covid.first_date', 'status', '__v']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      const formattedRegDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('date', formattedRegDate);
      setValue('blood_type', selectedRecord?.blood_type?._id);
      setValue('dob', getCurrentDateTime('date', selectedRecord.dob)?.split('-').reverse().join('-'));
      const checkboxFields = [
        'tattooing',
        'ear_piercing',
        'dental_extraction',
        'heart_disease',
        'diabetes',
        'sexually_transmitted_diseases',
        'lung_diseases',
        'allergic_diseases',
        'epilepsy',
        'jaundice',
        'fainting_spells',
        'cancer_malignant_disease',
        'blood_pressure',
        'kidney_disease',
        'blood_disorder',
        'antibiotics',
        'aspirin',
        'alcohol',
        'steroids',
        'vaccination',
        'dog_bites_rabies_vaccine',
        'major',
        'minor',
        'blood_transfusion'
      ];
      checkboxFields.forEach((field) => {
        const fieldValue = selectedRecord?.[field];
        setValue(field, fieldValue === 'true' || fieldValue === true);
      });
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    const editFlag = selectedRecord && currentAction === 'edit';
    try {
      const response = await request(editFlag ? `/donor-update` : '/donor-create', {
        method: editFlag ? 'PUT' : 'POST',
        params: selectedRecord?._id,
        body: formValues
      });
      if (response?.success && response.data) {
        toast(`Successfully ${editFlag ? 'edited' : 'created'} Donor Master`, { variant: 'success' });
        setSelectedRecord(response.data);
        setCurrentAction();
        reset();
      } else {
        toast(`Failed to ${editFlag ? 'edit' : 'create'} Donor Master`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting the form:', error);
      toast('An error occurred while processing your request', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setDonorDataObj({ loading: true });
    const response = await request('/donor-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setDonorDataObj({ loading: false, data: filteredData });
    } else {
      toast('Failed to load Retail Sale list', { variant: 'error' });
      setDonorDataObj({ loading: false });
    }
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/donor-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Donor Master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Donor Master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/donor-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Donor Master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Donor Master', { variant: 'error' });
    }
    setLoading(false);
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const watchDob = watch('dob');
  useEffect(() => {
    setValue('age', calculateAge(watchDob) || 0);
  });

  const handleChange = (e) => {
    setInput(e.target.value);
  };

  useEffect(() => {
    handleDonorSearch();
  }, [input]);

  const handleDonorSearch = async () => {
    const lowercasedInput = input.toLowerCase().trim();
    try {
      const response = await request('/donor-list', {
        method: 'GET',
        query: { name: lowercasedInput }
      });
      if (response.success && Array.isArray(response.data)) {
        setDonorDataObj({ loading: false, data: response.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setDonorDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setDonorDataObj({ loading: false, data: [] });
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Donor Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={handleQueryWindow}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff',
                height: '100vh'
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack sx={{ padding: '5px', borderRadius: '5px' }} spacing={0.5} direction="row">
                    <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Blood Type
                        </Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.8 }}
                          menus={bloodGroupDropdown.map((data) => ({ ...data, name: data?.group }))}
                          name="blood_type"
                          placeholder="Select Blood Type"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Patient Name
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="patient_name"
                          placeholder="Enter Patient Name"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Dob
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="dob"
                          type="date"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Gender
                        </Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.4 }}
                          name="gender"
                          placeholder="Select Gender"
                          menus={GENDER_DROPDOWN}
                        />
                        <TextField disabled sx={{ flex: 0.4 }} name="age" placeholder="Age" />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Country/State
                        </Typography>
                        <SelectTag
                          disabled
                          formControlSX={{ flex: 0.2 }}
                          name="country"
                          placeholder="Select Country"
                          menus={[{ _id: 'IN', name: 'India' }]}
                        />
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.6 }}
                          menus={getStatesByCountry(watch('country'))}
                          name="state"
                          placeholder="State"
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Occupation
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{
                            flex: 0.8
                          }}
                          name="occupation"
                          placeholder="Enter Occupation"
                        />
                      </Stack>

                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Mobile No.
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="mobile_no"
                          placeholder="Enter Mobile No"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Email
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="email"
                          placeholder="Enter Email"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Address
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="address"
                          placeholder="Enter Address"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1.2rem' }}>City</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          formControlSX={{ flex: 0.8 }}
                          menus={getCityByState(watch('country'), watch('state'))}
                          name="city"
                          placeholder="City"
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1.2rem' }}>Date/Time</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{
                            flex: 0.4
                          }}
                          name="date"
                          type="date"
                        />
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{
                            flex: 0.4
                          }}
                          name="time"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1.2rem' }}>Weight/Pulse</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{
                            flex: 0.4
                          }}
                          name="weight"
                        />
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{
                            flex: 0.4
                          }}
                          name="pulse"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1.2rem' }}>HB/Bp</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.4 }}
                          name="hb"
                          placeholder="Enter HB"
                        />
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.4 }}
                          name="bp"
                          placeholder="Select Bp"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Temprature
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="temp"
                          placeholder="Enter Temprature"
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1rem', fontSize: '1.2rem' }}>
                          Last Time Blood Donated
                        </Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          sx={{ flex: 0.8 }}
                          name="last_donated"
                          type="date"
                        />
                      </Stack>
                    </Grid>
                  </Stack>

                  <Grid container sx={{ width: '100%' }}>
                    <Grid item xs={6}>
                      <Box sx={{ border: '1px solid #ccc', padding: '1rem' }}>
                        <Stack direction="row" alignItems="center">
                          <Typography sx={{ flex: 0.4, fontWeight: 700, fontSize: '1.2rem', paddingRight: 1 }}>
                            Have You Donated Previously
                          </Typography>
                          <Radio
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.4 }}
                            name="donated_yes"
                            checked={selectedRadio === 'donated_yes'}
                            onChange={handleRadioChange}
                          />
                          <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', paddingRight: 2 }}>Yes</Typography>
                          <Radio
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.4 }}
                            name="donated_no"
                            checked={selectedRadio === 'donated_no'}
                            onChange={handleRadioChange}
                          />
                          <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>No</Typography>
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ border: '1px solid #ccc' }}>
                        <Stack direction="row" alignItems="center">
                          <Typography
                            sx={{ flex: 0.6, fontWeight: 700, fontSize: '1.2rem', paddingRight: 1, paddingLeft: 1 }}
                          >
                            In Last Six Months Have You Had any of Following
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                          {['tattooing', 'ear_piercing', 'dental_extraction'].map((item) => (
                            <Stack key={item} direction="row" spacing={0.25} alignItems="center">
                              <Checkbox disabled={!['new', 'edit'].includes(currentAction)} name={item} fontSize={18} />
                              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
                                {item.replace('_', ' ').toUpperCase()}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ border: '1px solid #ccc', padding: '1rem' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 1 }}>
                            Do you suffer from or have suffered from any of the following diseases?
                          </Typography>
                          <Grid container>
                            {[
                              { name: 'Heart Disease', field: 'heart_disease' },
                              { name: 'Diabetes', field: 'diabetes' },
                              { name: 'Sexually Transmitted Diseases', field: 'sexually_transmitted_diseases' },
                              { name: 'Lung Diseases', field: 'lung_diseases' },
                              { name: 'Allergic Diseases', field: 'allergic_diseases' },
                              { name: 'Epilepsy (Charay Rog)', field: 'epilepsy' },
                              { name: 'Jaundice (Last One Year)', field: 'jaundice' },
                              { name: 'Fainting Spells', field: 'fainting_spells' },
                              { name: 'Cancer/Malignant Disease', field: 'cancer_malignant_disease' },
                              { name: 'Blood Pressure', field: 'blood_pressure' },
                              { name: 'Kidney Disease', field: 'kidney_disease' },
                              { name: 'Blood Disorder', field: 'blood_disorder' }
                            ].map((disease) => (
                              <Grid item xs={4} key={disease.field}>
                                {' '}
                                <Stack direction="row" spacing={0.25} alignItems="center">
                                  <Checkbox
                                    disabled={!['new', 'edit'].includes(currentAction)}
                                    name={disease.field}
                                    fontSize={18}
                                  />
                                  <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>{disease.name}</Typography>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ border: '1px solid #ccc', padding: '1.92rem' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 1 }}>
                            Are you taking or have you taken any of these in the past 72 hours?
                          </Typography>
                          <Grid container spacing={6.7}>
                            {[
                              { name: 'Antibiotics', field: 'antibiotics' },
                              { name: 'Aspirin', field: 'aspirin' },
                              { name: 'Alcohol', field: 'alcohol' },
                              { name: 'Steroids', field: 'steroids' },
                              { name: 'Vaccination', field: 'vaccination' },
                              { name: 'Dog Bites Rabies Vaccine(1 year)', field: 'dog_bites_rabies_vaccine' }
                            ].map((medication) => (
                              <Grid item xs={4} key={medication.field}>
                                {' '}
                                <Stack direction="row" spacing={0.25} alignItems="center">
                                  <Checkbox
                                    disabled={!['new', 'edit'].includes(currentAction)}
                                    name={medication.field}
                                    fontSize={18}
                                  />
                                  <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
                                    {medication.name}
                                  </Typography>
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ border: '1px solid #ccc', padding: '1rem' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 1 }}>
                          Is there any history of surgery or blood transfusion in the past six months?
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { name: 'Major', field: 'major' },
                            { name: 'Minor', field: 'minor' },
                            { name: 'Blood Transfusion', field: 'blood_transfusion' }
                          ].map((surgery) => (
                            <Grid item xs={2.4} key={surgery.field}>
                              <Stack direction="row" spacing={0.25} alignItems="center">
                                <Checkbox
                                  disabled={!['new', 'edit'].includes(currentAction)}
                                  name={surgery.field}
                                  fontSize={18}
                                />
                                <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>{surgery.name}</Typography>
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Donor Master Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 10px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' 
              }
            }}
            label="Search Donor"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_COLUMNS}
          data={donorDataObj.data || []}
          loading={donorDataObj.loading}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
